import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, styled, Stack } from '@mui/material';
import { motion } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SpeedIcon from '@mui/icons-material/Speed';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';

// --- STYLED COMPONENTS ---

const HeroWrapper = styled(Box)({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  background: '#020617', // Ultra dark navy
  position: 'relative',
  overflow: 'hidden',
  padding: '120px 0 80px 0',
});

// Ambient Animated Background Glows
const MeshGradient = styled(motion.div)(({ color }) => ({
  position: 'absolute',
  width: '650px',
  height: '650px',
  background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
  filter: 'blur(100px)',
  opacity: 0.28,
  zIndex: 0,
  pointerEvents: 'none',
}));

const FloatingBadge = styled(motion.div)({
  padding: '10px 20px',
  borderRadius: '100px',
  background: 'rgba(15, 23, 42, 0.65)',
  border: '1px solid rgba(255, 255, 255, 0.12)',
  backdropFilter: 'blur(16px)',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '10px',
  color: '#2dd4bf',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
});

const PrimaryCTA = styled(motion(RouterLink))({
  padding: '16px 36px',
  borderRadius: '14px',
  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  color: '#ffffff',
  fontSize: '1rem',
  fontWeight: 700,
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '10px',
  boxShadow: '0 8px 25px -5px rgba(59, 130, 246, 0.5)',
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    boxShadow: '0 12px 35px -5px rgba(59, 130, 246, 0.7)',
  }
});

const SecondaryCTA = styled(motion(RouterLink))({
  padding: '16px 36px',
  borderRadius: '14px',
  background: 'rgba(255, 255, 255, 0.03)',
  border: '1px solid rgba(255, 255, 255, 0.12)',
  color: '#f8fafc',
  fontSize: '1rem',
  fontWeight: 600,
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '10px',
  backdropFilter: 'blur(12px)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.08)',
    borderColor: '#3b82f6',
  }
});

const StatCard = styled(motion.div)({
  padding: '16px 24px',
  borderRadius: '16px',
  background: 'rgba(15, 23, 42, 0.5)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  backdropFilter: 'blur(12px)',
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  textAlign: 'left',
});

// Framer Motion Stagger Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 25 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } 
  }
};

const ServicesHero = () => {
  return (
    <HeroWrapper>
      {/* Background Mesh Lighting */}
      <MeshGradient 
        color="#3b82f6"
        animate={{ x: [-150, 150, -150], y: [-80, 200, -80] }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
        style={{ top: '-15%', left: '-10%' }}
      />
      <MeshGradient 
        color="#8b5cf6"
        animate={{ x: [200, -200, 200], y: [300, -100, 300] }}
        transition={{ duration: 28, repeat: Infinity, ease: "easeInOut" }}
        style={{ bottom: '-15%', right: '-10%' }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Stack
          component={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          spacing={4}
          alignItems="center"
          textAlign="center"
        >
          {/* Status Badge */}
          <motion.div variants={itemVariants}>
            <FloatingBadge>
              <AutoAwesomeIcon sx={{ fontSize: '1.1rem' }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, letterSpacing: 1.5, textTransform: 'uppercase' }}>
                Engineering Modern Ecosystems
              </Typography>
            </FloatingBadge>
          </motion.div>

          {/* Headline */}
          <motion.div variants={itemVariants}>
            <Typography 
              variant="h1" 
              sx={{ 
                fontWeight: 900, 
                fontSize: { xs: '2.8rem', sm: '4.2rem', md: '5.8rem' },
                lineHeight: 1.05,
                letterSpacing: '-0.03em',
                color: '#f8fafc',
                maxWidth: '1000px',
                mx: 'auto'
              }}
            >
              Architecting <br />
              <span style={{ 
                background: 'linear-gradient(135deg, #60a5fa 0%, #2dd4bf 50%, #c084fc 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                Mission-Critical Tech
              </span>
            </Typography>
          </motion.div>

          {/* Subtitle */}
          <motion.div variants={itemVariants}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#94a3b8', 
                maxWidth: '740px', 
                mx: 'auto', 
                fontWeight: 400,
                lineHeight: 1.7,
                fontSize: { xs: '1rem', md: '1.25rem' }
              }}
            >
              From native Android systems with hardware integration to multi-platform cross-app ecosystems and cloud architectures. We build software engineered for scale.
            </Typography>
          </motion.div>

          {/* Call to Actions */}
          <motion.div variants={itemVariants}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} sx={{ pt: 1 }}>
              <PrimaryCTA 
                to="/portfolio"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                Start Your Project 
                <ArrowForwardIcon sx={{ fontSize: '1.1rem' }} />
              </PrimaryCTA>
              
              <SecondaryCTA 
                to="/services"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                Explore Capabilities
              </SecondaryCTA>
            </Stack>
          </motion.div>

          {/* Floating Performance Cards / Proof Metrics */}
          <Box sx={{ pt: 4, width: '100%', maxWidth: '850px' }}>
            <Stack 
              direction={{ xs: 'column', md: 'row' }} 
              spacing={2.5} 
              justifyContent="center"
              component={motion.div}
              variants={itemVariants}
            >
              <StatCard whileHover={{ y: -4 }}>
                <SpeedIcon sx={{ color: '#2dd4bf', fontSize: '2rem' }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 700 }}>
                    High-Throughput Code
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Optimized for sub-second UI & API responses
                  </Typography>
                </Box>
              </StatCard>

              <StatCard whileHover={{ y: -4 }}>
                <IntegrationInstructionsIcon sx={{ color: '#3b82f6', fontSize: '2rem' }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 700 }}>
                    Hardware & Cloud SDKs
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Zebra RFID, Scanner, and IoT integration
                  </Typography>
                </Box>
              </StatCard>
            </Stack>
          </Box>
        </Stack>
      </Container>

      {/* Grid Pattern Background overlay */}
      <Box sx={{ 
        position: 'absolute', 
        inset: 0, 
        backgroundImage: 'radial-gradient(rgba(255, 255, 255, 0.07) 1px, transparent 1px)',
        backgroundSize: '32px 32px',
        pointerEvents: 'none',
        opacity: 0.4
      }} />
    </HeroWrapper>
  );
};

export default ServicesHero;